import { Star } from '@phosphor-icons/react';
import { useStudentTheme } from '../context/StudentThemeContext';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_lesson-card-manager/artifacts/zd0x6jcg_c982a405-739a-4717-a6a0-d9774dc13353_20251118_231830_0000.png";

const LEVELS = [
    { name: 'Beginner', minProgress: 0, color: '#718096', logoBlur: 10, logoOpacity: 0.4 },
    { name: 'Rookie', minProgress: 15, color: '#D4A537', logoBlur: 6, logoOpacity: 0.55 },
    { name: 'Gevorderd', minProgress: 40, color: '#D69E2E', logoBlur: 3, logoOpacity: 0.7 },
    { name: 'Expert', minProgress: 70, color: '#38A169', logoBlur: 1, logoOpacity: 0.9 },
    { name: 'AutoPilot', minProgress: 100, color: '#D4A537', logoBlur: 0, logoOpacity: 1 },
];

function getLevel(progress) {
    let current = LEVELS[0];
    for (const level of LEVELS) {
        if (progress >= level.minProgress) current = level;
    }
    return current;
}

function getNextLevel(progress) {
    for (const level of LEVELS) {
        if (progress < level.minProgress) return level;
    }
    return null;
}

function getProgressToNextLevel(progress) {
    const current = getLevel(progress);
    const next = getNextLevel(progress);
    if (!next) return 100;
    const range = next.minProgress - current.minProgress;
    const done = progress - current.minProgress;
    return Math.round((done / range) * 100);
}

export default function LevelBadge({ progress, size = 'normal' }) {
    const theme = useStudentTheme();
    const rawLevel = getLevel(progress);
    const next = getNextLevel(progress);
    // Swap the brand-gold (#D4A537) in levels with the package theme primary
    const level = (rawLevel.color === '#D4A537')
        ? { ...rawLevel, color: theme.primary }
        : rawLevel;
    const progressToNext = getProgressToNextLevel(progress);
    const isAutoPilot = level.name === 'AutoPilot';

    if (size === 'small') {
        return (
            <span 
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold"
                style={{ backgroundColor: `${level.color}20`, color: level.color }}
                data-testid="level-badge-small"
            >
                <img 
                    src={LOGO_URL} 
                    alt="" 
                    className="w-4 h-4 object-contain"
                    style={{ 
                        opacity: level.logoOpacity,
                        filter: `blur(${level.logoBlur * 0.3}px)`
                    }}
                />
                {level.name}
            </span>
        );
    }

    return (
        <div 
            className="bg-[#1A202C] rounded-xl border border-[#4A5568] p-5"
            data-testid="level-badge"
        >
            <div className="flex items-center gap-4">
                {/* Logo icon - opacity grows with level */}
                <div className="relative">
                    <div 
                        className="w-16 h-16 rounded-xl flex items-center justify-center overflow-hidden"
                        style={{ backgroundColor: `${level.color}15`, border: `2px solid ${level.color}` }}
                    >
                        <img 
                            src={LOGO_URL}
                            alt={level.name}
                            className="w-14 h-14 object-contain transition-all duration-700"
                            style={{ 
                                opacity: level.logoOpacity,
                                filter: `blur(${level.logoBlur}px)`
                            }}
                        />
                    </div>
                    {isAutoPilot && (
                        <div className="absolute -top-1 -right-1 animate-bounce-pop">
                            <Star size={18} weight="fill" className="text-[#D4A537]" />
                        </div>
                    )}
                </div>

                {/* Level info */}
                <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs text-gray-500 uppercase tracking-wider">Level</span>
                    </div>
                    <h3 className="text-xl font-bold" style={{ color: level.color, fontFamily: 'Outfit, sans-serif' }}>
                        {level.name}
                    </h3>
                    
                    {next ? (
                        <div className="mt-2">
                            <div className="flex justify-between text-xs mb-1">
                                <span className="text-gray-400">Volgende: <span className="text-white font-medium">{next.name}</span></span>
                                <span className="text-gray-500">{progress}% / {next.minProgress}%</span>
                            </div>
                            <div className="w-full h-2 bg-[#2D3748] rounded-full overflow-hidden">
                                <div 
                                    className="h-full rounded-full"
                                    style={{ 
                                        backgroundColor: level.color,
                                        width: `${progressToNext}%`,
                                        transition: 'width 1s ease-out',
                                    }}
                                />
                            </div>
                        </div>
                    ) : (
                        <p className="text-sm text-[#D4A537] mt-1 font-medium italic">
                            "Drive like a pilot" - Maximaal level!
                        </p>
                    )}
                </div>
            </div>

            {/* Level progression - logo reveals step by step */}
            <div className="flex items-center justify-between mt-4 px-1">
                {LEVELS.map((lvl, i) => {
                    const isReached = progress >= lvl.minProgress;
                    const isCurrent = lvl.name === level.name;
                    return (
                        <div key={lvl.name} className="flex flex-col items-center gap-1">
                            <div
                                className={`w-10 h-10 rounded-full flex items-center justify-center border-2 overflow-hidden transition-all ${
                                    isCurrent
                                        ? 'border-[#D4A537] bg-[#D4A537]/20'
                                        : isReached 
                                            ? 'border-[#D4A537]/50 bg-[#D4A537]/10'
                                            : 'border-[#4A5568] bg-[#2D3748]'
                                }`}
                            >
                                <img 
                                    src={LOGO_URL}
                                    alt={lvl.name}
                                    className="w-7 h-7 object-contain transition-all duration-500"
                                    style={{ 
                                        opacity: isReached ? lvl.logoOpacity : 0.15,
                                        filter: isReached ? `blur(${lvl.logoBlur}px)` : 'blur(8px) grayscale(100%)'
                                    }}
                                />
                            </div>
                            <span className={`text-xs ${isCurrent ? 'text-white font-medium' : isReached ? 'text-gray-400' : 'text-gray-600'}`}>
                                {lvl.name}
                            </span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

export { getLevel, getNextLevel, LEVELS };
